import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { CHEMICAL_PALETTE, swatchTextColor } from '@peruselab/structure-editor/domain/chemicalPalette';
import { Icon } from './Icon';
import { useEscapeKey } from '../hooks/useEscapeKey';

/** Explicit artifact colors. This component never reads or changes the platform theme. */
export function ChemicalPalettePicker({
  value,
  onSelect
}: {
  readonly value: string;
  readonly onSelect: (color: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="icon-button"
        aria-label={t('chemicalPalette.title')}
        title={t('chemicalPalette.title')}
        onClick={() => setOpen(true)}
      >
        <Icon name="palette" />
      </button>
      {open ? <ChemicalPaletteDialog value={value} onSelect={onSelect} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/** The same swatches can open directly from a toolbar action, without another disclosure. */
export function ChemicalPaletteDialog({
  value,
  onSelect,
  onClose,
  returnFocusRef
}: {
  readonly value: string | undefined;
  readonly onSelect: (color: string) => void;
  readonly onClose: () => void;
  readonly returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  useEscapeKey(true, () => dialog.current?.close());
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="chemical-palette-dialog"
      aria-labelledby={headingId}
      onClose={() => {
        onClose();
        returnFocusRef?.current?.focus();
      }}
    >
      <div className="chemical-palette-heading">
        <h2 id={headingId}>{t('chemicalPalette.title')}</h2>
        <button
          type="button"
          className="icon-button"
          aria-label={t('chemicalPalette.close')}
          onClick={() => dialog.current?.close()}
        >
          <Icon name="close" />
        </button>
      </div>
      <p>{t('chemicalPalette.artifactHint')}</p>
      <div className="chemical-swatch-grid" role="group" aria-label={t('chemicalPalette.title')}>
        {CHEMICAL_PALETTE.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className="chemical-swatch"
            aria-pressed={value?.toLowerCase() === entry.color.toLowerCase()}
            title={`${entry.pigment} · ${entry.formula} · ${entry.color}`}
            style={{ backgroundColor: entry.color, color: swatchTextColor(entry.color) }}
            onClick={() => {
              onSelect(entry.color);
              dialog.current?.close();
            }}
          >
            <span>{entry.name}</span>
            <small>{entry.color}</small>
          </button>
        ))}
      </div>
    </dialog>
  );
}
